import Bugsnag from '@bugsnag/js';
import * as signalR from '@microsoft/signalr';
import React, { useCallback, useEffect, useRef } from 'react';
import { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { tokenSelector } from 'redux/global/auth/selectors';
import { BookingCancelledType, BookingDeletedType } from 'views/apps/Bookings/types';

type SignalRMethods = 'RecurringProcessed' | 'NewBooking' | 'BookingDeleted' | 'BookingEdited' | 'BookingCancelled';

export const recurringBookingsSignalR = 'RecurringProcessed';
export const newBookingSignalR = 'NewBooking';
export const bookingDeletedSignalR = 'BookingDeleted';
export const bookingEditedSignalR = 'BookingEdited';
export const bookingCancelledSingalR = 'BookingCancelled';

export type SignalRContextType = {
  startRecurringBookingsSignalR: (callback: () => void) => void;
  startNewBookingSignalR: (callback: (bookingId: number) => void) => void;
  startBookingDeletedSignalR: (callback: (data: BookingDeletedType) => void) => void;
  startBookingEditedSignalR: (callback: (bookingId: number) => void) => void;
  startBookingCancelledSignalR: (callback: (data: BookingCancelledType) => void) => void;
  stopSignalRMethod: (methodName: SignalRMethods, callback?: (...args: any[]) => void) => void;
};

type Props = PropsWithChildren<{}>;

export const SignalRContext = React.createContext<SignalRContextType>({
  startRecurringBookingsSignalR: () => {},
  startNewBookingSignalR: () => {},
  startBookingDeletedSignalR: () => {},
  startBookingEditedSignalR: () => {},
  startBookingCancelledSignalR: () => {},
  stopSignalRMethod: () => {},
});

const SignalRProvider = ({ children }: Props) => {
  const connection = useRef<signalR.HubConnection>();
  const connectionStartPromise = useRef<Promise<void>>();
  const callbacks = useRef(new Map<SignalRMethods, Set<(...args: any[]) => void>>());
  const token = useSelector(tokenSelector);

  const attachCallbacks = useCallback((hubConnection: signalR.HubConnection) => {
    callbacks.current.forEach((methodCallbacks, methodName) => {
      methodCallbacks.forEach((callback) => hubConnection.on(methodName, callback));
    });
  }, []);

  const startSignalRMethod = useCallback((methodName: SignalRMethods, callback: (...args: any[]) => void) => {
    let methodCallbacks = callbacks.current.get(methodName);
    if (!methodCallbacks) {
      methodCallbacks = new Set();
      callbacks.current.set(methodName, methodCallbacks);
    }

    if (!methodCallbacks.has(callback)) {
      methodCallbacks.add(callback);
      connection.current?.on(methodName, callback);
    }
  }, []);

  const stopSignalRMethod = useCallback((methodName: SignalRMethods, callback?: (...args: any[]) => void) => {
    if (callback) {
      callbacks.current.get(methodName)?.delete(callback);
      connection.current?.off(methodName, callback);
    } else {
      callbacks.current.get(methodName)?.clear();
      connection.current?.off(methodName);
    }
  }, []);

  const startRecurringBookingsSignalR = useCallback(
    (callback: () => void) => {
      startSignalRMethod(recurringBookingsSignalR, callback);
    },
    [startSignalRMethod],
  );

  const startNewBookingSignalR = useCallback(
    (callback: (bookingId: number) => void) => {
      startSignalRMethod(newBookingSignalR, callback);
    },
    [startSignalRMethod],
  );

  const startBookingDeletedSignalR = useCallback(
    (callback: (data: BookingDeletedType) => void) => {
      startSignalRMethod(bookingDeletedSignalR, callback);
    },
    [startSignalRMethod],
  );

  const startBookingEditedSignalR = useCallback(
    (callback: (bookingId: number) => void) => {
      startSignalRMethod(bookingEditedSignalR, callback);
    },
    [startSignalRMethod],
  );

  const startBookingCancelledSignalR = useCallback(
    (callback: (data: BookingCancelledType) => void) => {
      startSignalRMethod(bookingCancelledSingalR, callback);
    },
    [startSignalRMethod],
  );

  const startConnection = useCallback(() => {
    const currentConnection = connection.current;
    if (!token || !currentConnection || currentConnection.state !== signalR.HubConnectionState.Disconnected) {
      return Promise.resolve();
    }

    if (!connectionStartPromise.current) {
      connectionStartPromise.current = currentConnection
        .start()
        .catch((e) => {
          Bugsnag.notify(e);
        })
        .finally(() => {
          connectionStartPromise.current = undefined;
        });
    }

    return connectionStartPromise.current;
  }, [token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_DOMAIN}/bookings`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
          Math.min(1000 * Math.pow(2, previousRetryCount), 30000),
      })
      .build();
    connection.current = newConnection;
    attachCallbacks(newConnection);
    void startConnection();

    return () => {
      connectionStartPromise.current = undefined;
      if (connection.current === newConnection) {
        connection.current = undefined;
      }
      void newConnection.stop().catch((e) => Bugsnag.notify(e));
    };
  }, [attachCallbacks, startConnection, token]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void startConnection();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [startConnection]);

  return (
    <SignalRContext.Provider
      value={{
        startRecurringBookingsSignalR,
        stopSignalRMethod,
        startNewBookingSignalR,
        startBookingEditedSignalR,
        startBookingDeletedSignalR,
        startBookingCancelledSignalR,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
};

export default SignalRProvider;
